import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch } from "../../App";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Camera, Phone, Mail,
  Loader2, ClipboardList, MessageSquare, X, User, Users,
  Settings2, GripVertical, Minus, Star, History,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  API, fmtDateShort,
  DETAIL_COLS_DEF, DEFAULT_DETAIL_ORDER,
  DETAIL_COL_ORDER_KEY, DETAIL_MINIMIZED_KEY, LAPTOP_AUTO_MINIMIZE,
} from "./helpers";
import { AuthImage } from "./AuthImage";
import { ContactField } from "./ContactField";
import { InlineTask } from "./InlineTask";
import { InlineNote } from "./InlineNote";

export function CustomerDetail({ contactId, onClose, onRefreshList, editOnLoad, onEditConsumed }) {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [emails, setEmails] = useState([]);
  const [historie, setHistorie] = useState([]);
  const [showFullHistorie, setShowFullHistorie] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteTyp, setNoteTyp] = useState("Notiz");
  const [taskForm, setTaskForm] = useState({ titel: "", beschreibung: "", faellig_am: "" });
  const [emailForm, setEmailForm] = useState({ datum: new Date().toISOString().split("T")[0], betreff: "", text: "", absender: "", screenshot_path: null });
  const [emailScreenshot, setEmailScreenshot] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [viewEmail, setViewEmail] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [ansprechpartner, setAnsprechpartner] = useState([]);
  const [showAddAP, setShowAddAP] = useState(false);
  const [apForm, setApForm] = useState({ vorname: "", nachname: "", position: "", telefon: "", email: "", is_primary: false });
  const [editingAP, setEditingAP] = useState(null);
  const [crm, setCrm] = useState({
    contacts_visibility: "all", can_view_ansprechpartner: true,
    can_view_notes: true, can_view_emails: true, can_view_activities: true,
    can_edit_contacts: true, can_edit_ansprechpartner: true,
    can_create_tasks: true, can_edit_tasks: true,
  });

  // ─── Detail column order & minimized state ───
  const [detailOrder, setDetailOrder] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(DETAIL_COL_ORDER_KEY)); if (Array.isArray(v) && v.length === DEFAULT_DETAIL_ORDER.length) return v; } catch {}
    return [...DEFAULT_DETAIL_ORDER];
  });
  const [minimizedCols, setMinimizedCols] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(DETAIL_MINIMIZED_KEY)); if (Array.isArray(v)) return v; } catch {}
    return window.innerWidth < 1400 ? [...LAPTOP_AUTO_MINIMIZE] : [];
  });
  const detailDragCol = useRef(null);
  const detailDragOverCol = useRef(null);

  const toggleMinimize = useCallback((colId) => {
    setMinimizedCols((prev) => {
      const next = prev.includes(colId) ? prev.filter((c) => c !== colId) : [...prev, colId];
      localStorage.setItem(DETAIL_MINIMIZED_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const handleDetailColDrop = useCallback(() => {
    const from = detailDragCol.current;
    const to = detailDragOverCol.current;
    if (!from || !to || from === to) return;
    setDetailOrder((prev) => {
      const arr = [...prev];
      const fi = arr.indexOf(from);
      const ti = arr.indexOf(to);
      if (fi < 0 || ti < 0) return prev;
      arr.splice(fi, 1);
      arr.splice(ti, 0, from);
      localStorage.setItem(DETAIL_COL_ORDER_KEY, JSON.stringify(arr));
      return arr;
    });
    detailDragCol.current = null;
    detailDragOverCol.current = null;
  }, []);

  const showAllCols = useCallback(() => {
    setMinimizedCols([]);
    localStorage.setItem(DETAIL_MINIMIZED_KEY, JSON.stringify([]));
  }, []);

  const resetDetailLayout = useCallback(() => {
    const defOrder = [...DEFAULT_DETAIL_ORDER];
    setDetailOrder(defOrder);
    setMinimizedCols([]);
    localStorage.setItem(DETAIL_COL_ORDER_KEY, JSON.stringify(defOrder));
    localStorage.setItem(DETAIL_MINIMIZED_KEY, JSON.stringify([]));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [cRes, nRes, tRes, eRes, hRes, apRes] = await Promise.all([
      authFetch(`${API}/crm/contacts/${contactId}`),
      authFetch(`${API}/crm/contacts/${contactId}/notes`),
      authFetch(`${API}/crm/contacts/${contactId}/tasks`),
      authFetch(`${API}/crm/contacts/${contactId}/emails`),
      authFetch(`${API}/crm/contacts/${contactId}/historie`),
      authFetch(`${API}/crm/contacts/${contactId}/ansprechpartner`),
    ]);
    if (cRes.ok) { const d = await cRes.json(); setContact(d); setEditData(d); if (d._crm_permissions) setCrm(d._crm_permissions); }
    if (nRes.ok) setNotes(await nRes.json());
    if (tRes.ok) setTasks(await tRes.json());
    if (eRes.ok) setEmails(await eRes.json());
    if (hRes.ok) setHistorie(await hRes.json());
    if (apRes.ok) setAnsprechpartner(await apRes.json());
    setLoading(false);
  }, [contactId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (editOnLoad && !loading && contact) {
      setEditMode(true);
      onEditConsumed();
    }
  }, [editOnLoad, loading, contact, onEditConsumed]);

  const handleSave = async () => {
    setSaving(true);
    const res = await authFetch(`${API}/crm/contacts/${contactId}`, {
      method: "PUT", body: JSON.stringify(editData),
    });
    if (res.ok) { setEditMode(false); loadAll(); onRefreshList(); toast.success("Kontakt gespeichert"); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Kunde wirklich löschen?")) return;
    const res = await authFetch(`${API}/crm/contacts/${contactId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Kunde gelöscht"); onClose(); onRefreshList(); }
  };

  const toggleTask = async (t) => {
    const s = t.status === "erledigt" ? "offen" : "erledigt";
    await authFetch(`${API}/crm/contacts/${contactId}/tasks/${t.id}`, { method: "PUT", body: JSON.stringify({ status: s }) });
    loadAll(); onRefreshList();
  };

  const quickNote = async () => {
    if (!noteText.trim()) return;
    const res = await authFetch(`${API}/crm/contacts/${contactId}/notes`, {
      method: "POST", body: JSON.stringify({ text: noteText, aktivitaetstyp: noteTyp }),
    });
    if (res.ok) { setNoteText(""); setNoteTyp("Notiz"); setShowAddNote(false); loadAll(); onRefreshList(); toast.success(noteTyp === "Notiz" ? "Notiz erstellt" : `${noteTyp} dokumentiert`); }
  };

  const quickTask = async () => {
    if (!taskForm.titel.trim()) { toast.error("Titel erforderlich"); return; }
    const res = await authFetch(`${API}/crm/contacts/${contactId}/tasks`, {
      method: "POST", body: JSON.stringify({ ...taskForm, faellig_am: taskForm.faellig_am || null }),
    });
    if (res.ok) { setTaskForm({ titel: "", beschreibung: "", faellig_am: "" }); setShowAddTask(false); loadAll(); onRefreshList(); toast.success("Aufgabe erstellt"); }
  };

  const quickEmail = async () => {
    if (!emailForm.betreff.trim()) { toast.error("Betreff erforderlich"); return; }
    const res = await authFetch(`${API}/crm/contacts/${contactId}/emails`, {
      method: "POST", body: JSON.stringify(emailForm),
    });
    if (res.ok) {
      setEmailForm({ datum: new Date().toISOString().split("T")[0], betreff: "", text: "", absender: "", screenshot_path: null });
      setEmailScreenshot(null);
      setShowAddEmail(false);
      loadAll(); onRefreshList(); toast.success("Email dokumentiert");
    }
  };

  const analyzeScreenshot = async (file) => {
    setAnalyzing(true);
    const preview = URL.createObjectURL(file);
    setEmailScreenshot({ file, preview });
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await authFetch(`${API}/crm/contacts/${contactId}/analyze-email-screenshot`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setEmailForm((prev) => ({
          datum: data.datum || prev.datum,
          betreff: data.betreff || prev.betreff,
          text: data.text || prev.text,
          absender: data.absender || prev.absender,
          screenshot_path: data.screenshot_path || null,
        }));
        if (data.betreff || data.text) {
          toast.success("Screenshot analysiert");
        } else {
          toast.warning("Screenshot gespeichert – Text konnte nicht erkannt werden. Bitte manuell ausfüllen.");
        }
      } else {
        toast.error("Analyse fehlgeschlagen");
      }
    } catch {
      toast.error("Analyse fehlgeschlagen");
    }
    setAnalyzing(false);
  };

  const handleEmailPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        analyzeScreenshot(item.getAsFile());
        return;
      }
    }
  };

  const handleEmailDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) analyzeScreenshot(file);
  };

  if (loading) return <div className="flex items-center justify-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!contact) return null;

  const openTasks = tasks.filter((t) => t.status !== "erledigt");

  // ─── Ansprechpartner CRUD ───
  const handleAddAP = async () => {
    if (!apForm.vorname.trim() && !apForm.nachname.trim()) { toast.error("Bitte Vor- oder Nachname eingeben"); return; }
    const res = await authFetch(`${API}/crm/contacts/${contactId}/ansprechpartner`, {
      method: "POST", body: JSON.stringify(apForm),
    });
    if (res.ok) { toast.success("Ansprechpartner hinzugefügt"); setShowAddAP(false); setApForm({ vorname: "", nachname: "", position: "", telefon: "", email: "", is_primary: false }); loadAll(); onRefreshList(); }
    else toast.error("Fehler beim Hinzufügen");
  };

  const handleUpdateAP = async (apId, updates) => {
    const res = await authFetch(`${API}/crm/contacts/${contactId}/ansprechpartner/${apId}`, {
      method: "PUT", body: JSON.stringify(updates),
    });
    if (res.ok) { loadAll(); onRefreshList(); }
    else toast.error("Fehler beim Aktualisieren");
  };

  const handleDeleteAP = async (apId) => {
    const res = await authFetch(`${API}/crm/contacts/${contactId}/ansprechpartner/${apId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Ansprechpartner gelöscht"); loadAll(); onRefreshList(); }
    else toast.error("Fehler beim Löschen");
  };

  const handleTogglePrimary = async (apId) => {
    await handleUpdateAP(apId, { is_primary: true });
    toast.success("Hauptansprechpartner gesetzt");
  };

  const editFields = [
    { key: "firma", label: "Firma" }, { key: "vorname", label: "Vorname" },
    { key: "nachname", label: "Nachname" }, { key: "position", label: "Position" },
    { key: "telefon", label: "Telefon" }, { key: "mobil", label: "Mobil" },
    { key: "email", label: "E-Mail" }, { key: "webseite", label: "Webseite" },
    { key: "strasse", label: "Straße" }, { key: "plz", label: "PLZ" },
    { key: "ort", label: "Ort" },
  ];

  return (
    <div data-testid="customer-detail-panel" className="flex flex-col h-full">
      {/* Header */}
      <div className="px-2.5 py-1 border-b border-gray-200 flex items-center justify-between bg-white flex-shrink-0">
        <div className="min-w-0 flex items-center gap-2">
          <h3 className="text-[11px] font-semibold text-gray-900 truncate">
            {contact.firma || [contact.vorname, contact.nachname].filter(Boolean).join(" ") || "Kunde"}
          </h3>
          {contact.kundennummer && <span className="text-[10px] font-mono text-gray-400 bg-gray-50 px-1 py-0.5 rounded">{contact.kundennummer}</span>}
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {minimizedCols.length > 0 && (
            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-gray-500" onClick={showAllCols} data-testid="show-all-cols-btn">
              Alle anzeigen
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-gray-400" onClick={resetDetailLayout} title="Layout zurücksetzen" data-testid="reset-detail-layout-btn">
            <Settings2 className="w-3 h-3" />
          </Button>
          {crm.can_edit_contacts && (
            <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => setEditMode(!editMode)} data-testid="edit-contact-btn">
              <Pencil className="w-3 h-3" />
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-6 px-1.5 text-red-500 hover:text-red-700" onClick={handleDelete} data-testid="delete-contact-btn">
            <Trash2 className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={onClose} data-testid="close-detail-btn">
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Edit Mode */}
      {editMode ? (
        <div className="px-3 py-2 bg-gray-50 flex-shrink-0" data-testid="contact-edit-form">
          <div className="grid grid-cols-4 gap-x-3 gap-y-1.5 mb-2">
            {editFields.map((f) => (
              <div key={f.key}>
                <Label className="text-[10px] uppercase tracking-wider text-gray-400">{f.label}</Label>
                <Input value={editData[f.key] || ""} onChange={(e) => setEditData({ ...editData, [f.key]: e.target.value })} className="h-7 text-[11px]" data-testid={`edit-${f.key}`} />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-6 text-[11px] px-3" onClick={handleSave} disabled={saving} data-testid="save-contact-btn">{saving ? "Speichert..." : "Speichern"}</Button>
            <Button variant="outline" size="sm" className="h-6 text-[11px] px-3" onClick={() => { setEditMode(false); setEditData(contact); }}>Abbrechen</Button>
          </div>
        </div>
      ) : (
        /* Flexible Column Layout */
        <div className="flex flex-1 min-h-0 gap-0" data-testid="detail-columns">
          {detailOrder.map((colId) => {
            const def = DETAIL_COLS_DEF.find((d) => d.id === colId);
            if (!def) return null;
            // CRM permission-based column visibility
            if (colId === "ansprechpartner" && !crm.can_view_ansprechpartner) return null;
            if (colId === "notizen" && !crm.can_view_notes) return null;
            if (colId === "emails" && !crm.can_view_emails) return null;
            if (colId === "historie" && !crm.can_view_activities) return null;
            const ColIcon = def.icon;
            const isMin = minimizedCols.includes(colId);

            if (isMin) {
              return (
                <div
                  key={colId}
                  className="flex-shrink-0 w-10 bg-gray-50/80 border-r border-gray-100 flex flex-col items-center cursor-pointer hover:bg-gray-100 transition-all duration-200"
                  onClick={() => toggleMinimize(colId)}
                  title={def.title}
                  data-testid={`col-${colId}`}
                >
                  <ColIcon className="w-3.5 h-3.5 text-gray-400 mt-2 flex-shrink-0" />
                  <span
                    className="text-[9px] text-gray-400 font-medium mt-1.5 select-none whitespace-nowrap"
                    style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
                  >
                    {def.title}
                  </span>
                </div>
              );
            }

            return (
              <div
                key={colId}
                className="flex-1 min-w-0 flex flex-col border-r border-gray-100 transition-all duration-200"
                data-testid={`col-${colId}`}
              >
                {/* Column Header: drag + minimize */}
                <div
                  className="flex items-center justify-between px-2 py-0.5 bg-gray-50/50 border-b border-gray-100 flex-shrink-0 cursor-grab select-none"
                  draggable
                  onDragStart={() => { detailDragCol.current = colId; }}
                  onDragOver={(e) => { e.preventDefault(); detailDragOverCol.current = colId; }}
                  onDrop={(e) => { e.preventDefault(); handleDetailColDrop(); }}
                  data-testid={`col-header-${colId}`}
                >
                  <div className="flex items-center gap-1 min-w-0">
                    <GripVertical className="w-3 h-3 text-gray-300 flex-shrink-0" />
                    <ColIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider truncate">{def.title}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleMinimize(colId); }}
                    className="text-gray-300 hover:text-gray-600 flex-shrink-0 ml-1"
                    title="Minimieren"
                    data-testid={`minimize-${colId}`}
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                </div>
                {/* Column Content */}
                <div className="px-2 py-1.5 overflow-y-auto flex-1 min-h-0">
                  {colId === "stammdaten" && (
                    <>
                      <ContactField label="Firma" value={contact.firma} />
                      <ContactField label="Kontaktperson" value={[contact.vorname, contact.nachname].filter(Boolean).join(" ")} />
                      <ContactField label="Verantwortlicher" value={contact.verantwortlicher} />
                      <ContactField label="Position" value={contact.position} />
                      <ContactField label="Telefon" value={contact.telefon} />
                      <ContactField label="E-Mail" value={contact.email} link={contact.email ? `mailto:${contact.email}` : null} />
                      <ContactField label="Straße" value={contact.strasse || contact.adresse} />
                      <ContactField label="PLZ / Ort" value={[contact.plz, contact.ort].filter(Boolean).join(" ")} />
                      <ContactField label="Website" value={contact.webseite} link={contact.webseite ? (contact.webseite.startsWith("http") ? contact.webseite : `https://${contact.webseite}`) : null} />
                      <ContactField label="Kd-Nr." value={contact.kundennummer} />
                    </>
                  )}
                  {colId === "ansprechpartner" && (
                    <>
                      <div className="flex items-center justify-end mb-0.5">
                      {crm.can_edit_ansprechpartner && (
                        <Button variant="outline" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => { setEditingAP(null); setApForm({ vorname: "", nachname: "", position: "", telefon: "", email: "", is_primary: false }); setShowAddAP(true); }} data-testid="quick-ap-btn">
                          <Plus className="w-2.5 h-2.5 mr-0.5" /> Neu
                        </Button>
                      )}
                      </div>
                      {ansprechpartner.length === 0 ? (
                        <p className="text-[11px] text-gray-400">Keine Ansprechpartner</p>
                      ) : (
                        <div className="space-y-1.5">
                          {ansprechpartner.map((ap) => (
                            <div key={ap.id} className="pb-1.5 border-b border-gray-50 group" data-testid={`ap-${ap.id}`}>
                              <div className="flex items-center gap-1">
                                {ap.is_primary && <Star className="w-3 h-3 text-amber-400 fill-amber-400 flex-shrink-0" />}
                                <p className="text-[11px] text-gray-900 font-medium leading-tight truncate flex-1">
                                  {[ap.vorname, ap.nachname].filter(Boolean).join(" ")}
                                </p>
                                {crm.can_edit_ansprechpartner && (
                                <div className="hidden group-hover:flex gap-0.5 flex-shrink-0">
                                  {!ap.is_primary && (
                                    <button onClick={() => handleTogglePrimary(ap.id)} className="text-gray-300 hover:text-amber-500" title="Als Hauptansprechpartner setzen" data-testid={`ap-primary-${ap.id}`}>
                                      <Star className="w-3 h-3" />
                                    </button>
                                  )}
                                  <button onClick={() => { setEditingAP(ap); setApForm({ vorname: ap.vorname, nachname: ap.nachname, position: ap.position, telefon: ap.telefon, email: ap.email, is_primary: ap.is_primary }); setShowAddAP(true); }} className="text-gray-300 hover:text-blue-500" data-testid={`ap-edit-${ap.id}`}>
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => handleDeleteAP(ap.id)} className="text-gray-300 hover:text-red-500" data-testid={`ap-delete-${ap.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                                )}
                              </div>
                              {ap.position && <p className="text-[10px] text-gray-500 leading-tight truncate">{ap.position}</p>}
                              {ap.telefon && <p className="text-[10px] text-gray-500 leading-tight truncate"><Phone className="w-2.5 h-2.5 inline mr-0.5" />{ap.telefon}</p>}
                              {ap.email && <p className="text-[10px] leading-tight truncate"><a href={`mailto:${ap.email}`} className="text-blue-600 hover:underline"><Mail className="w-2.5 h-2.5 inline mr-0.5" />{ap.email}</a></p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {colId === "aufgaben" && (
                    <>
                      {crm.can_create_tasks && (
                        <div className="flex items-center justify-end mb-0.5">
                          <Button variant="outline" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => setShowAddTask(true)} data-testid="quick-aufgabe-btn">
                            <Plus className="w-2.5 h-2.5 mr-0.5" /> Aufgabe
                          </Button>
                        </div>
                      )}
                      {openTasks.length === 0 ? (
                        <p className="text-[11px] text-gray-400">Keine offenen Aufgaben</p>
                      ) : (
                        <div className="space-y-1.5">
                          {openTasks.map((t) => (
                            <InlineTask key={t.id} task={t} contactId={contactId} onToggle={() => toggleTask(t)} onChanged={() => { loadAll(); onRefreshList(); }} />
                          ))}
                        </div>
                      )}
                      {tasks.filter((t) => t.status === "erledigt").length > 0 && (
                        <p className="text-[10px] text-gray-400 mt-1.5 border-t border-gray-50 pt-0.5">
                          {tasks.filter((t) => t.status === "erledigt").length} erledigt
                        </p>
                      )}
                    </>
                  )}
                  {colId === "notizen" && (
                    <>
                      <div className="flex items-center justify-end mb-0.5">
                        <Button variant="outline" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => setShowAddNote(true)} data-testid="quick-notiz-btn">
                          <Plus className="w-2.5 h-2.5 mr-0.5" /> Notiz
                        </Button>
                      </div>
                      {notes.length === 0 ? (
                        <p className="text-[11px] text-gray-400">Keine Notizen</p>
                      ) : (
                        <div className="space-y-1.5">
                          {notes.map((n) => (
                            <InlineNote key={n.id} note={n} contactId={contactId} onChanged={() => { loadAll(); onRefreshList(); }} />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {colId === "emails" && (
                    <>
                      <div className="flex items-center justify-end mb-0.5">
                        <Button variant="outline" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => setShowAddEmail(true)} data-testid="quick-email-btn">
                          <Plus className="w-2.5 h-2.5 mr-0.5" /> Email
                        </Button>
                      </div>
                      {emails.length === 0 ? (
                        <p className="text-[11px] text-gray-400">Keine Emails</p>
                      ) : (
                        <div className="space-y-1">
                          {emails.map((e) => (
                            <div
                              key={e.id}
                              className="pb-1 border-b border-gray-50 cursor-pointer hover:bg-blue-50/50 rounded px-0.5 -mx-0.5 transition-colors"
                              onClick={() => setViewEmail(e)}
                              data-testid={`email-${e.id}`}
                            >
                              <p className="text-[10px] text-gray-400 whitespace-nowrap">{e.datum} · {e.created_by}</p>
                              <p className="text-[11px] text-gray-800 font-medium leading-tight truncate">{e.betreff}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {colId === "historie" && (
                    <>
                      {historie.length === 0 ? (
                        <p className="text-[11px] text-gray-400">Keine Einträge</p>
                      ) : (
                        <div className="space-y-1">
                          {historie.map((h) => (
                            <div key={h.id} className="pb-1 border-b border-gray-50" data-testid={`historie-${h.id}`}>
                              <p className="text-[10px] text-gray-400 whitespace-nowrap">{fmtDateShort(h.created_at)}</p>
                              <p className="text-[11px] text-gray-800 leading-tight truncate">
                                {h.aktion} – <span className="text-gray-500">{h.benutzer}</span>
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      {historie.length > 5 && (
                        <Button variant="link" size="sm" className="h-auto p-0 mt-1 text-[11px] text-blue-600" onClick={() => setShowFullHistorie(true)} data-testid="show-full-historie-btn">
                          Gesamte Historie anzeigen
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <Dialog open={showAddAP} onOpenChange={(v) => { setShowAddAP(v); if (!v) setEditingAP(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-[14px]">{editingAP ? "Ansprechpartner bearbeiten" : "Ansprechpartner hinzufügen"}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px]">Vorname</Label>
                <Input value={apForm.vorname} onChange={(e) => setApForm({ ...apForm, vorname: e.target.value })} className="h-7 text-[11px]" data-testid="ap-vorname" />
              </div>
              <div>
                <Label className="text-[11px]">Nachname</Label>
                <Input value={apForm.nachname} onChange={(e) => setApForm({ ...apForm, nachname: e.target.value })} className="h-7 text-[11px]" data-testid="ap-nachname" />
              </div>
            </div>
            <div>
              <Label className="text-[11px]">Position</Label>
              <Input value={apForm.position} onChange={(e) => setApForm({ ...apForm, position: e.target.value })} className="h-7 text-[11px]" data-testid="ap-position" />
            </div>
            <div>
              <Label className="text-[11px]">Telefon</Label>
              <Input value={apForm.telefon} onChange={(e) => setApForm({ ...apForm, telefon: e.target.value })} className="h-7 text-[11px]" data-testid="ap-telefon" />
            </div>
            <div>
              <Label className="text-[11px]">E-Mail</Label>
              <Input value={apForm.email} onChange={(e) => setApForm({ ...apForm, email: e.target.value })} className="h-7 text-[11px]" data-testid="ap-email" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="ap-primary" checked={apForm.is_primary} onChange={(e) => setApForm({ ...apForm, is_primary: e.target.checked })} className="rounded border-gray-300" data-testid="ap-is-primary" />
              <Label htmlFor="ap-primary" className="text-[11px] cursor-pointer">Hauptansprechpartner</Label>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" className="text-[11px]" onClick={() => {
              if (editingAP) { handleUpdateAP(editingAP.id, apForm); setShowAddAP(false); setEditingAP(null); }
              else handleAddAP();
            }} data-testid="ap-save-btn">{editingAP ? "Speichern" : "Hinzufügen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-[14px]">Notiz hinzufügen</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-[11px]">Aktivitätstyp</Label>
              <select
                value={noteTyp}
                onChange={(e) => setNoteTyp(e.target.value)}
                className="w-full h-8 text-[12px] border border-gray-200 rounded-md px-2 bg-white"
                data-testid="note-aktivitaetstyp"
              >
                <option value="Notiz">Notiz</option>
                <option value="Telefonat">Telefonat</option>
                <option value="Email">Email</option>
                <option value="Sonstiges">Sonstiges</option>
              </select>
            </div>
            <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Text eingeben..." className="text-[12px] min-h-[80px]" data-testid="quick-note-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowAddNote(false)}>Abbrechen</Button>
            <Button size="sm" onClick={quickNote} data-testid="quick-note-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-[14px]">Neue Aufgabe</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-[11px]">Titel</Label>
              <Input value={taskForm.titel} onChange={(e) => setTaskForm({ ...taskForm, titel: e.target.value })} className="h-8 text-[12px]" data-testid="quick-task-titel" />
            </div>
            <div>
              <Label className="text-[11px]">Beschreibung</Label>
              <Textarea value={taskForm.beschreibung} onChange={(e) => setTaskForm({ ...taskForm, beschreibung: e.target.value })} rows={2} className="text-[12px]" />
            </div>
            <div>
              <Label className="text-[11px]">Fällig am</Label>
              <Input type="date" value={taskForm.faellig_am} onChange={(e) => setTaskForm({ ...taskForm, faellig_am: e.target.value })} className="h-8 text-[12px]" data-testid="quick-task-faellig" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowAddTask(false)}>Abbrechen</Button>
            <Button size="sm" onClick={quickTask} data-testid="quick-task-save">Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddEmail} onOpenChange={(v) => { setShowAddEmail(v); if (!v) { setEmailScreenshot(null); setAnalyzing(false); } }}>
        <DialogContent className="max-w-lg" onPaste={handleEmailPaste}>
          <DialogHeader><DialogTitle className="text-[14px]">Email dokumentieren</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div
              className={`border-2 border-dashed rounded-md p-3 text-center transition-colors ${
                analyzing ? "border-blue-300 bg-blue-50" : emailScreenshot ? "border-green-300 bg-green-50" : "border-gray-200 hover:border-gray-400 bg-gray-50"
              }`}
              onDrop={handleEmailDrop}
              onDragOver={(e) => e.preventDefault()}
              data-testid="email-screenshot-zone"
            >
              {analyzing ? (
                <div className="flex items-center justify-center gap-2 text-[11px] text-blue-600">
                  <Loader2 className="w-4 h-4 animate-spin" /> Analysiere Screenshot...
                </div>
              ) : emailScreenshot ? (
                <div className="space-y-1.5">
                  <img src={emailScreenshot.preview} alt="Screenshot" className="max-h-[120px] mx-auto rounded border border-gray-200" data-testid="email-screenshot-preview" />
                  <p className="text-[10px] text-green-600">Screenshot erkannt und analysiert</p>
                  <button className="text-[10px] text-red-500 hover:underline" onClick={() => { setEmailScreenshot(null); setEmailForm((f) => ({ ...f, screenshot_path: null })); }}>Entfernen</button>
                </div>
              ) : (
                <label className="cursor-pointer block">
                  <Camera className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                  <p className="text-[11px] text-gray-500">Screenshot einfügen (Strg+V), per Drag & Drop oder</p>
                  <p className="text-[11px] text-blue-600 hover:underline mt-0.5">Datei auswählen</p>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) analyzeScreenshot(e.target.files[0]); }} data-testid="email-screenshot-upload" />
                </label>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px]">Datum</Label>
                <Input type="date" value={emailForm.datum} onChange={(e) => setEmailForm({ ...emailForm, datum: e.target.value })} className="h-8 text-[12px]" data-testid="quick-email-datum" />
              </div>
              <div>
                <Label className="text-[11px]">Absender</Label>
                <Input value={emailForm.absender} onChange={(e) => setEmailForm({ ...emailForm, absender: e.target.value })} className="h-8 text-[12px]" placeholder="name@firma.de" data-testid="quick-email-absender" />
              </div>
            </div>
            <div>
              <Label className="text-[11px]">Betreff</Label>
              <Input value={emailForm.betreff} onChange={(e) => setEmailForm({ ...emailForm, betreff: e.target.value })} className="h-8 text-[12px]" data-testid="quick-email-betreff" />
            </div>
            <div>
              <Label className="text-[11px]">Text</Label>
              <Textarea value={emailForm.text} onChange={(e) => setEmailForm({ ...emailForm, text: e.target.value })} rows={4} className="text-[12px]" placeholder="Email-Text hier einfügen oder per Screenshot erkennen..." data-testid="quick-email-text" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowAddEmail(false)}>Abbrechen</Button>
            <Button size="sm" onClick={quickEmail} disabled={analyzing} data-testid="quick-email-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showFullHistorie} onOpenChange={setShowFullHistorie}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[14px]">Gesamte Historie</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {historie.map((h) => (
              <div key={h.id} className="flex items-center gap-3 py-1.5 border-b border-gray-50" data-testid={`full-historie-${h.id}`}>
                <p className="text-[11px] text-gray-400 font-mono w-20 flex-shrink-0">{fmtDateShort(h.created_at)}</p>
                <p className="text-[11px] text-gray-700">{h.aktion} – <span className="text-gray-500">{h.benutzer}</span></p>
              </div>
            ))}
            {historie.length === 0 && <p className="text-[11px] text-gray-400 py-4 text-center">Keine Einträge</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Detail Modal - 2-Column Layout */}
      <Dialog open={!!viewEmail} onOpenChange={(v) => { if (!v) { setViewEmail(null); setLightboxOpen(false); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden p-0" data-testid="email-detail-modal">
          {viewEmail && (
            <div className="flex flex-col h-full max-h-[85vh]">
              <div className="px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-4 text-[11px] text-gray-500 mb-1.5">
                  <span>{viewEmail.datum}</span>
                  {viewEmail.absender && <span>von: <span className="text-gray-700 font-medium">{viewEmail.absender}</span></span>}
                  <span className="ml-auto text-gray-400">Erfasst von: {viewEmail.created_by}</span>
                </div>
                <h3 className="text-[14px] font-semibold text-gray-900" data-testid="email-detail-betreff">{viewEmail.betreff}</h3>
              </div>
              <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                <div className="overflow-y-auto p-4 bg-gray-50/50" data-testid="email-detail-left">
                  {viewEmail.screenshot_path ? (
                    <AuthImage
                      path={viewEmail.screenshot_path}
                      alt="Email Screenshot"
                      className="w-full rounded border border-gray-200 bg-white cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxOpen(true)}
                      title="Klicken für Vollansicht"
                      data-testid="email-detail-screenshot"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400 text-[12px]">
                      <Mail className="w-5 h-5 mr-2" /> Kein Screenshot vorhanden
                    </div>
                  )}
                </div>
                <div className="overflow-y-auto p-4 flex flex-col" data-testid="email-detail-right">
                  <div className="flex items-center justify-between mb-2 flex-shrink-0">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Erkannter Text</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[10px] px-2"
                      onClick={() => { navigator.clipboard.writeText(viewEmail.text || ""); toast.success("Text kopiert"); }}
                      data-testid="email-detail-copy"
                    >
                      <ClipboardList className="w-3 h-3 mr-1" /> Kopieren
                    </Button>
                  </div>
                  <div className="flex-1 overflow-y-auto bg-white rounded-md border border-gray-100 p-3">
                    <p className="text-[12px] text-gray-700 whitespace-pre-wrap leading-relaxed" data-testid="email-detail-text">{viewEmail.text || "Kein Text erkannt"}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox for Screenshot fullscreen */}
      {lightboxOpen && viewEmail?.screenshot_path && (
        <div
          className="fixed inset-0 z-[99999] bg-black/80 flex items-center justify-center cursor-pointer"
          onClick={() => setLightboxOpen(false)}
          data-testid="email-lightbox"
        >
          <AuthImage
            path={viewEmail.screenshot_path}
            alt="Email Screenshot Vollansicht"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="email-lightbox-img"
          />
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            onClick={() => setLightboxOpen(false)}
            data-testid="email-lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
}
